"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { FadeInUp } from "./AnimatedSection";
import { plans } from "@/data/pricing";

export default function Pricing() {
  return (
    <section id="pricing" className="py-12 sm:py-16 md:py-20 bg-surface relative overflow-hidden">
      {/* Glow effects at top */}
      <div className="absolute top-0 left-1/4 w-96 h-48 bg-red-accent/10 blur-[100px] pointer-events-none" />
      <div className="absolute top-0 right-1/4 w-96 h-48 bg-purple/10 blur-[100px] pointer-events-none" />

      <div className="max-w-[960px] mx-auto px-6">
        <FadeInUp>
          <div className="text-center mb-8 md:mb-16">
            <p className="section-badge mb-4 mx-auto">Pricing Plans</p>
            <h2 className="text-2xl sm:text-3xl md:text-[48px] font-bold leading-[1.2] font-ui">
              <span className="font-serif italic text-purple-light">Unbeatable</span>
              <span> Value Unmatched </span>
              <span className="font-serif italic text-purple-light">Quality</span>
            </h2>
          </div>
        </FadeInUp>

        {/* Cards */}
        <div className="flex justify-center gap-4 md:gap-6 pb-4 flex-wrap lg:flex-nowrap">
          {plans.map((plan, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              viewport={{ once: true }}
              className={`w-full sm:w-[300px] lg:w-[340px] rounded-3xl p-5 sm:p-6 md:p-8 flex flex-col ${
                plan.featured
                  ? "ring-2 ring-purple/30 sm:ring-0 border border-purple/60 bg-gradient-to-b from-purple/20 via-surface-light to-purple/20 shadow-[0px_8px_24px_0px_rgba(255,255,255,0.09)_inset,0px_16px_40px_0px_rgba(225,29,36,0.15)] md:-mt-4 md:mb-4 md:scale-[1.02]"
                  : "border border-foreground/10 bg-surface-light"
              }`}
            >
              {plan.featured && (
                <p className="text-xs font-medium mb-2 text-badge-text">{plan.tagline}</p>
              )}
              <p className="text-3xl sm:text-4xl font-bold mb-1">{plan.price}</p>
              {!plan.featured && (
                <p className="text-xs text-foreground/50 mb-2">{plan.tagline}</p>
              )}
              <h3 className="text-lg font-semibold mb-6">{plan.name}</h3>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feature, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-foreground/80">
                    <svg className="w-4 h-4 mt-0.5 text-red-accent flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              <Link
                href={plan.href}
                className={`inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full font-semibold text-sm transition-colors ${
                  plan.featured
                    ? "bg-purple text-white hover:bg-purple/80"
                    : "bg-red-accent/10 text-red-accent border border-red-accent/30 hover:bg-red-accent/20"
                }`}
              >
                Explore More
                <Image src="/assets/arrows/CTA-Arrow-white.svg" alt="" width={12} height={12} className={plan.featured ? "theme-invert" : ""} />
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Link to full pricing page */}
        <FadeInUp delay={0.3} className="text-center mt-10">
          <Link
            href="/pricing"
            className="inline-flex items-center gap-2 text-sm font-semibold text-red-accent hover:text-red-accent/80 transition-colors group"
          >
            View detailed pricing & comparison
            <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </FadeInUp>
      </div>
    </section>
  );
}
